import {
  useCallback,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";
import { Label } from "@/components/atoms/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/molecules/select";
import { EDITOR_CONTENT_SELECT_TRIGGER_CLASS } from "@/lib/ui/editorContentChrome";
import { cn } from "@/lib/utils";

export interface DirectiveOptionFieldAllowedValuesOption {
  value: string;
  label: string;
}

export interface DirectiveOptionFieldAllowedValuesProps {
  options: DirectiveOptionFieldAllowedValuesOption[];
  value: string;
  onChange: (value: string) => void;
  label?: ReactNode;
  /** Optional id for the trigger (e.g. for aria-labelledby). */
  id?: string;
  placeholder?: string;
  /** Ref to the Select trigger button (for programmatic focus). */
  triggerRef?: RefObject<HTMLButtonElement | null>;
  /** Focus the trigger on mount (e.g. when advancing to this slot). */
  autoFocus?: boolean;
}

/** Radix Select forbids value=""; use this sentinel for the default option and map to "" externally. */
const SENTINEL_DEFAULT = "__default__";

/**
 * Strict allowed-values dropdown for directive option slots.
 * User must pick one of the options. Use value="" for the default option (e.g. "Sentence style (default)").
 */
export const DirectiveOptionFieldAllowedValues = ({
  options,
  value,
  onChange,
  label,
  id,
  placeholder = "Select…",
  triggerRef,
  autoFocus = false,
}: DirectiveOptionFieldAllowedValuesProps): ReactNode => {
  const localTriggerRef = useRef<HTMLButtonElement | null>(null);
  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | null) => {
      localTriggerRef.current = node;
      if (triggerRef != null) {
        triggerRef.current = node;
      }
    },
    [triggerRef],
  );

  useLayoutEffect(() => {
    if (!autoFocus) return;
    const el = triggerRef?.current ?? localTriggerRef.current;
    if (el != null) {
      el.focus({ preventScroll: true });
      return;
    }
    const idFrame = requestAnimationFrame(() => {
      (triggerRef?.current ?? localTriggerRef.current)?.focus({
        preventScroll: true,
      });
    });
    return () => cancelAnimationFrame(idFrame);
  }, [autoFocus, triggerRef]);

  const valueInOptions = options.some((o) => o.value === value);
  const normalizedValue = valueInOptions ? value : "";
  const selectValue =
    normalizedValue === "" ? SENTINEL_DEFAULT : normalizedValue;

  return (
    <div className="space-y-2">
      {label != null && (
        <Label
          variant="caption"
          htmlFor={id}
          id={id != null ? `${id}-label` : undefined}
        >
          {label}
        </Label>
      )}
      <Select
        value={selectValue}
        onValueChange={(v) => {
          onChange(v === SENTINEL_DEFAULT ? "" : v);
        }}
      >
        <SelectTrigger
          ref={setTriggerRef}
          id={id}
          data-editor-select-trigger=""
          aria-labelledby={id != null ? `${id}-label` : undefined}
          className={cn("w-full", EDITOR_CONTENT_SELECT_TRIGGER_CLASS)}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent dropdownPanel>
          {options.map((opt) => (
            <SelectItem
              key={opt.value === "" ? SENTINEL_DEFAULT : opt.value}
              value={opt.value === "" ? SENTINEL_DEFAULT : opt.value}
            >
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
